/**
 * useInstallPromptScheduler
 * Hook to manage the 15-day install prompt schedule
 */

import { useEffect, useState } from 'react'
import { logger } from '@shared/lib/utils/logger'

const STORAGE_KEY = 'pwa-install-prompt-dismissed-at'
const DAYS_INTERVAL = 15
const MS_PER_DAY = 24 * 60 * 60 * 1000

interface InstallPromptScheduler {
  shouldShowPrompt: boolean
  dismissPrompt: () => void
  resetSchedule: () => void
}

/**
 * Manages the install prompt scheduling logic
 * Shows prompt every 15 days if not installed
 */
export function useInstallPromptScheduler(
  isInstalled: boolean,
  isInstallable: boolean
): InstallPromptScheduler {
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false)

  useEffect(() => {
    // Don't show if app is already installed
    if (isInstalled) {
      setShouldShowPrompt(false)
      return
    }

    // Don't show if install is not available
    if (!isInstallable) {
      setShouldShowPrompt(false)
      return
    }

    // Check if enough time has passed since last dismissal
    const lastDismissedAt = localStorage.getItem(STORAGE_KEY)

    if (!lastDismissedAt) {
      // Never dismissed before, show prompt
      setShouldShowPrompt(true)
      return
    }

    try {
      const lastDismissedDate = new Date(lastDismissedAt)
      const now = new Date()
      const daysSinceLastDismissal = (now.getTime() - lastDismissedDate.getTime()) / MS_PER_DAY

      if (daysSinceLastDismissal >= DAYS_INTERVAL) {
        // 15 days have passed, show prompt again
        setShouldShowPrompt(true)
      } else {
        setShouldShowPrompt(false)
      }
    } catch (error) {
      // Invalid date in localStorage, clear it and show prompt
      logger.error('Invalid date in localStorage:', error)
      localStorage.removeItem(STORAGE_KEY)
      setShouldShowPrompt(true)
    }
  }, [isInstalled, isInstallable])

  /**
   * User dismissed the prompt, store timestamp
   */
  const dismissPrompt = () => {
    const now = new Date().toISOString()
    localStorage.setItem(STORAGE_KEY, now)
    setShouldShowPrompt(false)
  }

  /**
   * Reset the schedule (for testing or manual override)
   */
  const resetSchedule = () => {
    localStorage.removeItem(STORAGE_KEY)
    setShouldShowPrompt(true)
  }

  return {
    shouldShowPrompt,
    dismissPrompt,
    resetSchedule,
  }
}
